import { getRangePayload } from '../range/rangeState.js';

/**
 * Display formatting and input sync for GenericDatepicker.
 */

// Token set must mirror GenericDateTime._formatWithTokens — longest-first so
// 'MMMM' wins over 'MM', 'HH' over 'H', etc.
const TOKEN_PATTERN = /YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g;

// Tokens that render digits — each produces a fixed-width mask segment.
const MASK_WIDTH = {
  YYYY: 4, YY: 2,
  MMMM: 5, MMM: 3,
  MM: 2, M: 2,
  DD: 2, D: 2,
  HH: 2, H: 2,
  hh: 2, h: 2,
  mm: 2, m: 2,
  ss: 2, s: 2,
  A: 2, a: 2,
};

/** Validate template characters against the known token set. Returns true if OK. */
export function validateDisplayFormat(template) {
  if (!template) return true;
  // Strip all known tokens (longest-first alternation); any remaining Latin
  // letter means a typo'd/unknown token (e.g. 'WW').
  const stripped = template.replace(TOKEN_PATTERN, '');
  return !/[a-zA-Z]/.test(stripped);
}

/**
 * Build the empty-state mask from an explicit template: every token becomes
 * dashes (fixed width), literals are kept verbatim.
 * 'MM/DD/YYYY HH:mm' → '--/--/-- --:--'
 */
function maskFromTemplate(template) {
  let out = '';
  let lastIndex = 0;
  TOKEN_PATTERN.lastIndex = 0;
  let match;
  while ((match = TOKEN_PATTERN.exec(template)) !== null) {
    out += template.slice(lastIndex, match.index);
    out += '-'.repeat(MASK_WIDTH[match[0]] ?? 2);
    lastIndex = TOKEN_PATTERN.lastIndex;
  }
  out += template.slice(lastIndex);
  return out;
}

/** Compute the display template for the picker: explicit > derived default. */
function resolveDisplayTemplate(picker) {
  const explicit = picker.config.displayFormat;
  if (explicit) return explicit;
  return buildDisplayFormat(picker.config.display);
}

/** Default (legacy) template — derived from display config; 12-hour + AM/PM. */
function buildDisplayFormat(display) {
  const dateParts = [];
  if (display.month) dateParts.push('MM');
  if (display.date) dateParts.push('DD');
  if (display.year) dateParts.push('YYYY');

  const timeParts = [];
  if (display.hours) timeParts.push('hh');
  if (display.minutes) timeParts.push('mm');
  if (display.seconds) timeParts.push('ss');

  const parts = [];
  if (dateParts.length > 0) parts.push(dateParts.join('/'));
  if (timeParts.length > 0) {
    parts.push(`${timeParts.join(':')}${display.hours ? ' A' : ''}`);
  }
  return parts.join(' ');
}

export function formatDisplayDate(picker, date) {
  const template = resolveDisplayTemplate(picker);

  if (!date) {
    if (picker.config.displayFormat) {
      return maskFromTemplate(picker.config.displayFormat);
    }
    const d = picker.config.display;
    const parts = [];
    const dateParts = [];
    if (d.month) dateParts.push('--');
    if (d.date) dateParts.push('--');
    if (d.year) dateParts.push('--');
    if (dateParts.length > 0) parts.push(dateParts.join('/'));

    const timeParts = [];
    if (d.hours) timeParts.push('--');
    if (d.minutes) timeParts.push('--');
    if (d.seconds) timeParts.push('--');
    if (timeParts.length > 0) parts.push(timeParts.join(':'));

    return parts.join(' ');
  }

  return window.GenericDateTime.from(date).withFormat(template).asString();
}

export function syncInputValue(picker) {
  if (picker.config.range) {
    const sep = picker.config.rangeSeparator ?? ' – ';
    const { startDate, endDate } = picker.state;
    if (!startDate && !endDate) {
      picker.$input.val('');
    } else if (startDate && !endDate) {
      picker.$input.val(`${formatDisplayDate(picker, startDate)}${sep}…`);
    } else {
      picker.$input.val(
        `${formatDisplayDate(picker, startDate)}${sep}${formatDisplayDate(picker, endDate)}`,
      );
    }
    if (picker.config.onChange) picker.config.onChange(getRangePayload(picker), picker);
    return;
  }

  if (!picker.state.currentDate) {
    picker.$input.val(formatDisplayDate(picker, null));
    if (picker.config.onChange) picker.config.onChange(null, picker);
    return;
  }

  picker.$input.val(formatDisplayDate(picker, picker.state.currentDate));
  if (picker.config.onChange) picker.config.onChange(picker.state.currentDate, picker);
}
